import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { TextInput } from '~/components/forms';
import { FormFeedback } from './FormFeedback';
import { FormField } from './FormField';
import { getFieldError } from './formUtils';
import { FormFieldProps } from './interfaces';

export interface TransferListOption {
  value: string | number;
  label: string;
}

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  availableOptions: readonly TransferListOption[];
  availableLabel?: string;
  selectedLabel?: string;
  filterable?: boolean;
  onChange?: (selected: TransferListOption[]) => void;
}

interface ListBoxProps {
  items: TransferListOption[];
  onItemClick: (item: TransferListOption) => void;
  placeholder?: string;
  searchTerm?: string;
  cyTag?: string;
}

const ListBox = ({ items, onItemClick, placeholder, searchTerm, cyTag }: ListBoxProps) => {
  if (items.length === 0) {
    return (
      <div className="p-4 text-center text-grey-700 body-text">
        {placeholder || 'No items available'}
      </div>
    );
  }

  return (
    <div className="overflow-y-auto max-h-[300px]">
      {items.map((item) => {
        if (searchTerm && !item.label.toLowerCase().includes(searchTerm.toLowerCase())) {
          return null;
        }
        return (
          <div
            key={item.value}
            data-cy={`${cyTag}-${item.label}`}
            // [MCV-1525] One click moves the item straight across — it used to
            // take a click to highlight then a second click to transfer.
            onClick={() => onItemClick(item)}
            className={classNames(
              'px-3 py-2 cursor-pointer body-text border-b border-grey-900 last:border-b-0',
              'hover:bg-grey-900 transition-colors'
            )}
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );
};

const BUTTON_STYLES = classNames(
  'p-2 rounded-md border border-grey-800',
  'hover:bg-grey-900 active:bg-grey-800',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
  'transition-colors duration-150'
);

export const TransferList = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    label,
    availableOptions,
    availableLabel = 'Available',
    selectedLabel = 'Selected',
    onChange,
    required,
    filterable,
    tooltip,
    cyTag,
    className,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;

  const {
    watch,
    formState: { errors },
  } = useFormContext();

  const error = getFieldError(errors, name);

  const availableSearch = watch('root.availableSearch');
  const selectedSearch = watch('root.selectedSearch');

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const selectedItems: TransferListOption[] = field.value || [];
        const availableItems = availableOptions.filter(
          (option) => !selectedItems.find((item) => item.value === option.value)
        );

        const commit = (next: TransferListOption[]) => {
          field.onChange(next);
          onChange?.(next);
        };

        // [MCV-1525] Row click transfers one item; the double-arrow buttons
        // transfer the whole list. There is deliberately no single-item
        // "move highlighted" step — clicking a row IS the move, so the old
        // highlight-then-arrow buttons (and the selection state behind them)
        // were removed rather than left permanently disabled.
        const moveRight = (item: TransferListOption) => commit([...selectedItems, item]);
        const moveLeft = (item: TransferListOption) =>
          commit(selectedItems.filter((s) => s.value !== item.value));
        const moveAllRight = () => commit([...selectedItems, ...availableItems]);
        const moveAllLeft = () => commit([]);

        return (
          <FormField
            id={name}
            label={label}
            required={required}
            tooltip={tooltip}
            className={className}
            labelFor={false}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${name}-error`}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={props.description}
                helperId={`${name}-helper`}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div
              id={name}
              role="group"
              aria-labelledby={label ? `${name}-label` : undefined}
              aria-invalid={!!error || undefined}
              aria-required={required || undefined}
              aria-describedby={
                error?.message ? `${name}-error` : props.description ? `${name}-helper` : undefined
              }
              aria-errormessage={error?.message ? `${name}-error` : undefined}
              className="flex items-center gap-4"
            >
              {/* Available Items */}
              <div className="flex-1">
                {filterable && (
                  <TextInput
                    name="root.availableSearch"
                    placeholder="Search Available..."
                    className="my-4"
                  />
                )}
                <div className="border border-grey-800 rounded-md bg-white min-h-[300px]">
                  <ListBox
                    items={availableItems}
                    onItemClick={moveRight}
                    placeholder="No items available"
                    searchTerm={availableSearch}
                    cyTag={cyTag}
                  />
                </div>
                <div className="body-text-sb text-grey-500 my-3">
                  {availableLabel} ({availableItems.length})
                </div>
              </div>

              {/* Move-all controls */}
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={moveAllRight}
                  disabled={availableItems.length === 0}
                  className={BUTTON_STYLES}
                  title="Move all right"
                >
                  <ChevronDoubleRightIcon className="h-5 w-5 text-grey-100" />
                </button>
                <button
                  type="button"
                  onClick={moveAllLeft}
                  disabled={selectedItems.length === 0}
                  className={BUTTON_STYLES}
                  title="Move all left"
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5 text-grey-100" />
                </button>
              </div>

              {/* Selected Items */}
              <div className="flex-1">
                {filterable && (
                  <TextInput
                    name="root.selectedSearch"
                    placeholder="Search Selected..."
                    className="my-4"
                  />
                )}
                <div className="border border-grey-800 rounded-md bg-white min-h-[300px]">
                  <ListBox
                    cyTag={cyTag ? `${cyTag}-selected` : undefined}
                    items={selectedItems}
                    onItemClick={moveLeft}
                    placeholder="No items selected"
                    searchTerm={selectedSearch}
                  />
                </div>
                <div className="body-text-sb text-grey-500 my-3">
                  {selectedLabel} ({selectedItems.length})
                </div>
              </div>
            </div>
          </FormField>
        );
      }}
    />
  );
};
